import { Avatar, Box, Button, Card, Flex, Text } from '@homebotapp/hb-react-component-catalog'
import { defineMessages, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { selectNewestPrequal } from '../../../store/selectors/prequal'
import { selectCustomerProfile } from '../../../store/selectors/customerProfile'
import { CustomerProfileData } from '../../../types/customerProfile'
import { useTrackingContext } from '../../../providers/tracking'
import { Player } from '../../../Components/shared/Player'
import { selectPrequalVideoId, selectPrequalVideoType, selectPrequalVideoUrl } from '../../../store/selectors/videos'
import { formatCurrency } from '../../../helpers/formatters'
import { useMemo } from 'react'
import Finance from '../../../quartermaster/src/calculations/finance'
import { CUSTOMER_MESSAGES } from '../../../constants/customerMessages'
import { useSafeVerificationFlow } from '../../../Components/shared/Questions/hooks/useSafeVerificationFlow'
import { VerificationFlowProvider, VerificationFlowManager } from '../../../Components/shared/VerificationFlow'
import { useVerificationFlowEnabled } from '../../../hooks/useVerificationFlowEnabled'
import {
  selectPreferredLoanOfficerMembership,
  selectPreferredRealEstateAgentMembership
} from '../../../store/selectors/clientTeamMemberships'
import { useCustomerBranding } from '../../../hooks/homebotNetwork/usePreferredAgentOrCustomer'

const MSG = defineMessages({
  qualifiedFor: {
    id: 'for_you_content.budget_modal.qualified_for',
    defaultMessage: 'You are pre-qualified for'
  },
  notQualifiedHeader: {
    id: 'for_you_content.budget_modal.not_qualified_header',
    defaultMessage: 'Ready for a pre-qual?'
  },
  notQualifiedBody: {
    id: 'for_you_content.budget_modal.not_qualified_body',
    defaultMessage:
      'Pre-qualifying gives you a clear budget with no obligation to proceed, and makes you stand out as a serious buyer.'
  },
  monthlyPayment: {
    id: 'for_you_content.budget_modal.monthly_payment',
    defaultMessage: '{amount}/mo (principal & interest)'
  },
  loanDetails: {
    id: 'for_you_content.budget_modal.loan_details',
    defaultMessage: '{loanType} • {loanTerm} yr fixed • {interestRate}%'
  },
  requestUpdatedPrequalification: {
    id: 'for_you_content.budget_modal.request_updated_prequalification',
    defaultMessage: 'Request an updated pre-qual'
  },
  prequalify: {
    id: 'for_you_content.budget_modal.prequalify',
    defaultMessage: 'Prequalify'
  },
  prequalRequestSent: {
    id: 'for_you_content.budget_modal.prequal_request_sent',
    defaultMessage: 'Pre-qual request sent!'
  },
  willBeInTouchSoon: {
    id: 'for_you_content.budget_modal.will_be_in_touch_soon',
    defaultMessage: '{name} will be in touch soon'
  },
  yourLoanOfficer: {
    id: 'for_you_content.budget_modal.your_loan_officer',
    defaultMessage: 'Your loan officer'
  },
  fail: {
    id: 'for_you_content.budget_modal.fail',
    defaultMessage: 'Failed to send pre-qual request'
  }
})

const PrequalificationCardComponent = () => {
  const intl = useIntl()

  const prequalification = useSelector(selectNewestPrequal)
  const branding = useCustomerBranding('LoanOfficer')
  const photoUri = branding?.photoUri
  const videoId = useSelector(selectPrequalVideoId)
  const videoType = useSelector(selectPrequalVideoType)
  const videoUrl = useSelector(selectPrequalVideoUrl)
  const loanOfficerMembership = useSelector(selectPreferredLoanOfficerMembership)

  // Use verification flow
  const { startVerification, state: verificationState } = useSafeVerificationFlow()

  // get monthly payment using prequalification term, rate, and loan amount
  const monthlyPayment = useMemo(() => {
    if (prequalification?.maxLoanAmount && prequalification?.loanTermMonths && prequalification?.apr) {
      return Finance.calculatePayment(
        prequalification?.maxLoanAmount,
        prequalification?.loanTermMonths,
        prequalification?.apr
      )
    } else {
      return null
    }
  }, [prequalification])

  const handlePrequalRequest = async () => {
    // Use verification flow - it handles all fallbacks internally
    if (startVerification && verificationState.currentStep === 'idle') {
      const message = prequalification?.maxHomePrice
        ? CUSTOMER_MESSAGES.BUYER_WITH_PREQUAL.message.defaultMessage
        : CUSTOMER_MESSAGES.HBN_BUYER_PREQUAL_REQUEST.message.defaultMessage

      let fallbackTopicKey
      if (loanOfficerMembership) {
        fallbackTopicKey = CUSTOMER_MESSAGES.HBN_BUYER_PREQUAL_REQUEST.topicKey
      } else {
        fallbackTopicKey = prequalification?.maxHomePrice
          ? CUSTOMER_MESSAGES.BUYER_WITH_PREQUAL.topicKey // fallback for existing prequal
          : CUSTOMER_MESSAGES.BUYER_PREQUAL_REQUEST.topicKey // fallback for new prequal
      }

      startVerification(
        message,
        CUSTOMER_MESSAGES.HBN_BUYER_PREQUAL_REQUEST.topicKey, // verified topic
        fallbackTopicKey,
        'LoanOfficer'
      )
    }
  }

  return (
    <Card p={4} my={4} sx={{ backgroundColor: 'var(--hb-colors-card-bg-layer-2) !important' }}>
      <Flex flexDirection='column' gap={4} alignItems='center' bgColor='card-bg-layer-3' p={4} borderRadius={16}>
        {prequalification?.maxHomePrice ? (
          <Box>
            <Text m={0} color='neutral.900' fontWeight={'bold'}>
              {intl.formatMessage(MSG.qualifiedFor)}{' '}
              <Text as='span' m={0} fontWeight={'bold'} color='success'>
                {formatCurrency(prequalification.maxHomePrice)}
              </Text>
            </Text>
            {monthlyPayment && (
              <Text m={0} size='sm'>
                {intl.formatMessage(MSG.monthlyPayment, {
                  amount: formatCurrency(monthlyPayment)
                })}
              </Text>
            )}
            <Text m={0} size='sm'>
              {intl.formatMessage(MSG.loanDetails, {
                loanType: prequalification.loanType,
                loanTerm: prequalification.loanTermMonths / 12,
                interestRate: prequalification?.apr
              })}
            </Text>
          </Box>
        ) : (
          <Flex>
            <Avatar mr='16px' src={photoUri} />
            <Box>
              <Text m={0} fontWeight={'bold'} color='neutral.900'>
                {intl.formatMessage(MSG.notQualifiedHeader)}
              </Text>
              <Text m={0} size='sm'>
                {intl.formatMessage(MSG.notQualifiedBody)}
              </Text>
            </Box>
          </Flex>
        )}

        {!prequalification?.maxHomePrice && Boolean(videoType && videoId) && (
          <Box my={1} width='100%'>
            <Player videoType={videoType} videoId={videoId} videoUrl={videoUrl} />
          </Box>
        )}

        <Button
          width='100%'
          color='brand.700'
          onClick={handlePrequalRequest}
          _hover={{ color: 'neutral.50', backgroundColor: 'brand.700' }}
          mt={4}
          tracking={{
            guid: '4fkr9H6eamiPmxKAegdh4D',
            ui_context: 'Budget Modal',
            descriptor: 'Update Budget Submit Button Clicked'
          }}
        >
          {prequalification?.maxHomePrice
            ? intl.formatMessage(MSG.requestUpdatedPrequalification)
            : intl.formatMessage(MSG.prequalify)}
        </Button>
      </Flex>
    </Card>
  )
}

export const PrequalificationCard = () => {
  const loanOfficerMembership = useSelector(selectPreferredLoanOfficerMembership)
  const realEstateAgentMembership = useSelector(selectPreferredRealEstateAgentMembership)
  const verificationEnabled = useVerificationFlowEnabled()

  return (
    <VerificationFlowProvider
      options={{
        enableVerificationFlow: verificationEnabled,
        initialPartnerType: 'LoanOfficer',
        loanOfficerMembership,
        realEstateAgentMembership,
        includePrequal: true
      }}
    >
      <VerificationFlowManager />
      <PrequalificationCardComponent />
    </VerificationFlowProvider>
  )
}
